'use client'

// OBSOLETE FILE - replaced by Slider component

import { useMedia } from '@/hooks'
import { cn } from '@/lib/utils'
import { Base, ButtonIcon } from '@/ui/components'
import { MEDIA } from '@spot'
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { memo, useCallback, useEffect, useEffectEvent, useState } from 'react'
import Container from '../Base/Container'
import Title from '../Base/Title'
import { BaseProps } from '../types'
interface Props extends BaseProps {
  children?: React.ReactNode
  sliderClass?: any
  sliderOptions?: EmblaOptionsType
  variant?:
    | 'topEvent'
    | 'story'
    | 'content'
    | 'tour'
    | 'hotel'
    | 'event'
    | 'restaurant'
  showTitle?: boolean
  title?: string
  showArrows?: boolean
  showProgressBar?: boolean
  slidesPerView?: number
  spaceBetween?: string
}

const FeatureHorizontalSlide = ({
  children,
  variant,
  showTitle,
  sliderOptions,
  title,
  sliderClass,
  className,
  showArrows = true,
  showProgressBar = true,
  slidesPerView = 4,
  spaceBetween = '1rem',
  ...props
}: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(sliderOptions)
  //states
  const [scrollProgress, setScrollProgress] = useState(0)
  const [disabledNext, setDisabledNext] = useState<boolean>(false)
  const [disabledPrev, setDisabledPrev] = useState<boolean>(false)
  const [navigationHidden, setNavigationHidden] = useState<boolean>(false)

  const isDesktop = useMedia(MEDIA.desktop, true)

  //composers
  const sectionClass = cn('', className)

  const checkDisabled = useCallback(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.canScrollPrev() ? setDisabledPrev(false) : setDisabledPrev(true)
    emblaApi.canScrollNext() ? setDisabledNext(false) : setDisabledNext(true)
  }, [emblaApi])

  const checkNav = useCallback(() => {
    if (emblaApi) {
      if (isDesktop) {
        emblaApi.canScrollNext() === false && setNavigationHidden(true)
      } else {
        setNavigationHidden(false)
      }
    }
  }, [isDesktop, emblaApi])

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('init', checkNav)
      emblaApi.on('resize', checkNav)
    }
  }, [emblaApi, checkNav])

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', checkDisabled)
      emblaApi.on('init', checkDisabled)
    }
  }, [emblaApi, checkDisabled])

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
    setScrollProgress(progress * 75)
  }, [])

  const handleOnScroll = useEffectEvent((emblaApi: EmblaCarouselType) => {
    if (!emblaApi) {
      return
    }
    onScroll(emblaApi)
  })

  useEffect(() => {
    if (!emblaApi) {
      return
    }
    handleOnScroll(emblaApi)
    emblaApi
      .on('reInit', handleOnScroll)
      .on('scroll', handleOnScroll)
      .on('slideFocus', handleOnScroll)
  }, [emblaApi])

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev()
    }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
  }, [emblaApi])

  return (
    <Base {...props} className={sectionClass} anim="fade-in">
      <Container className="pb-md relative">
        {showTitle && title && (
          <Title as="h2" className={cn('mb-lg')}>
            {title}
          </Title>
        )}
        <div className="relative">
          <div
            className={cn('embla')}
            ref={emblaRef}
            style={{
              '--slides-per-view': `${100 / slidesPerView}%`,
              '--slides-per-view-md': `${(100 / slidesPerView) * 2}%`,
              '--slide-spacing': spaceBetween
            }}
          >
            <div
              className={cn(
                '-ml-(--slide-spacing) grid auto-cols-[100%] grid-flow-col md:auto-cols-(--slides-per-view-md) lg:auto-cols-(--slides-per-view)',
                sliderClass
              )}
            >
              {children}
            </div>
            {showArrows && !navigationHidden && (
              <div
                className="mb-lg gap-2xs absolute right-0 bottom-full z-5 grid grid-cols-2"
                data-arrows
              >
                <ButtonIcon
                  onClick={scrollPrev}
                  disabled={disabledPrev}
                  icon="material-symbols:arrow-back-ios-new"
                  size="m"
                />

                <ButtonIcon
                  onClick={scrollNext}
                  disabled={disabledNext}
                  icon="material-symbols:arrow-forward-ios"
                  size="m"
                />
              </div>
            )}
            {showProgressBar && !navigationHidden && (
              <div className="absolute bottom-0 z-2 w-full">
                <div className="relative mx-auto grid h-1 w-full max-w-56 overflow-hidden bg-neutral-200">
                  <div
                    className="absolute left-0 h-full w-1/4 bg-neutral-600"
                    style={{
                      left: `${scrollProgress}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </Base>
  )
}

export default memo(FeatureHorizontalSlide)
